import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  DocumentLanguageFilterType,
  DocumentsCountryFilterType,
  DocumentSortByFiterType,
  DocumentUserLevelFilterType,
} from 'src/Libs/downloads/models'
import { LabeledOption } from 'src/Types/globalTypes'

export const useDocumentOptions = (
  isFilter?: boolean
): {
  languageOptions: LabeledOption<DocumentLanguageFilterType>[]
  userLevelOptions: LabeledOption<DocumentUserLevelFilterType>[]
  countryOptions: LabeledOption<DocumentsCountryFilterType>[]
  sortOptions: LabeledOption<DocumentSortByFiterType>[]
} => {
  const { t } = useCustomTranslation()

  const languageOptions: LabeledOption<DocumentLanguageFilterType>[] = [
    {
      value: 'de',
      label: t('common.languages.german'),
    },
    {
      value: 'en',
      label: t('common.languages.english'),
    },
    {
      value: 'nl',
      label: t('common.languages.dutch'),
    },
    {
      value: 'fr',
      label: t('common.languages.french'),
    },
    {
      value: 'xx',
      label: t('documents.filter.languages.other'),
    },
  ]

  const userLevelOptions: LabeledOption<DocumentUserLevelFilterType>[] = [
    { value: 'user', label: t('documents.filter.user-level.user') },
    { value: 'installer_partner', label: t('documents.filter.user-level.installer-partner') },
    { value: 'e3dc_service', label: t('documents.filter.user-level.e3dc-service') },
  ]

  const countryOptions: LabeledOption<DocumentsCountryFilterType>[] = [
    { value: 'DEU', label: t('common.countries.short.DEU') },
    { value: 'AUT', label: t('common.countries.short.AUT') },
    { value: 'NLD', label: t('common.countries.short.NLD') },
    { value: 'FRA', label: t('common.countries.short.FRA') },
    { value: 'CHE', label: t('common.countries.short.CHE') },
    { value: 'ESP', label: t('common.countries.short.ESP') },
    { value: 'XXX', label: t('documents.filter.countries.other') },
  ]

  const sortOptions: LabeledOption<DocumentSortByFiterType>[] = [
    { value: 'name_asc', label: t('documents.filter.sort-by.name-asc') },
    { value: 'name_desc', label: t('documents.filter.sort-by.name-desc') },
    { value: 'language_asc', label: t('documents.filter.sort-by.language-asc') },
    { value: 'language_desc', label: t('documents.filter.sort-by.language-desc') },
    { value: 'country_asc', label: t('documents.filter.sort-by.country-asc') },
    { value: 'country_desc', label: t('documents.filter.sort-by.country-desc') },
    { value: 'upload_date_asc', label: t('documents.filter.sort-by.upload-date-asc') },
    { value: 'upload_date_desc', label: t('documents.filter.sort-by.upload-date-desc') },
  ]

  if (isFilter) {
    languageOptions.push({
      value: 'all',
      label: t('documents.filter.languages.all'),
    })
    userLevelOptions.push({ value: 'all', label: t('documents.filter.user-level.all') })
    countryOptions.push({ value: 'ALL', label: t('documents.filter.countries.all') })
  }

  return { userLevelOptions, languageOptions, countryOptions, sortOptions }
}

interface useDocumentFetchReturn {
  loading: boolean
  deleting: boolean
  deleteCategory: (categoryId: number) => Promise<void>
  updateCategory: (categoryId: number, prio?: number, parentIdF?: number) => Promise<void>
  updateDocument: (documentId: number, prio?: number, parentId?: number) => Promise<void>
  deleteDocument: (documentId: number) => Promise<void>
}

export const useDocumentFetch = (): useDocumentFetchReturn => {
  const [loading, setLoading] = useState<boolean>(false)
  const [deleting, setDeleting] = useState<boolean>(false)
  const authFetch = useAuthFetchWithResult()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const dispatch = useDispatch()
  const { t } = useCustomTranslation()

  const deleteCategory = async (categoryId: number): Promise<void> => {
    setDeleting(true)
    try {
      await authFetch({ path: `files/categories/${categoryId}`, method: 'DELETE' })
      enqueueSuccessStack(t('documents.success.deleteCategory'))
      dispatch(refreshApiView())
    } catch (err) {
      const error = err as AuthFetchError
      enqueueErrorStack(t('documents.error.deleteCategory', { errorCode: error.code, message: error.message }))
    } finally {
      setDeleting(false)
    }
  }

  const updateCategory = async (categoryId: number, prio?: number, parentId?: number): Promise<void> => {
    setLoading(true)
    try {
      const payload = new FormData()
      if (prio !== undefined) payload.append('prio', `${prio}`)
      if (parentId !== undefined) payload.append('parent_id', `${parentId}`)

      await authFetch({ path: `files/categories/${categoryId}`, method: 'PUT', payload })
      enqueueSuccessStack(t('documents.success.updateCategory'))
      dispatch(refreshApiView())
    } catch (err) {
      const error = err as AuthFetchError
      enqueueErrorStack(t('documents.error.updateCategory', { errorCode: error.code, message: error.message }))
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateDocument = async (documentId: number, prio?: number, parentId?: number): Promise<void> => {
    setLoading(true)
    try {
      const payload = new FormData()
      if (prio !== undefined) payload.append('prio', `${prio}`)
      if (parentId !== undefined) payload.append('parent_id', `${parentId}`)

      await authFetch({ path: `files/items/${documentId}`, method: 'PUT', payload })
      enqueueSuccessStack(t('documents.success.updateDocument'))
      dispatch(refreshApiView())
    } catch (err) {
      const error = err as AuthFetchError
      enqueueErrorStack(t('documents.error.updateDocument', { errorCode: error.code, message: error.message }))
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteDocument = async (documentId: number): Promise<void> => {
    setDeleting(true)
    try {
      await authFetch({ path: `files/items/${documentId}`, method: 'DELETE' })
      enqueueSuccessStack(t('documents.success.deleteDocument'))
      dispatch(refreshApiView())
    } catch (err) {
      const error = err as AuthFetchError
      enqueueErrorStack(t('documents.error.deleteDocument', { errorCode: error.code, message: error.message }))
    } finally {
      setDeleting(false)
    }
  }

  return { loading, deleting, updateCategory, deleteCategory, updateDocument, deleteDocument }
}
